import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { queueOnboardingAfterSignup } from '@/lib/onboardingJourney';
import {
  openGoogleAuthWindow,
  persistGoogleAuthSession,
  resolveGoogleAuthFrontendOrigin,
  subscribeGoogleAuthResult,
} from '@/lib/googleAuthOAuth';
import { isDesktopApp } from '@/services/config/env';
import { TwinneeWordmark } from '@/components/TwinneeWordmark';

function GoogleMark() {
  return (
    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export const AuthPage = ({ mode: initialMode }) => {
  const [mode, setMode] = useState(initialMode || 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const googlePopupRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('storycraft_token');
    if (token) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/auth/google/status')
      .then(({ data }) => {
        if (!cancelled) setGoogleEnabled(Boolean(data?.enabled));
      })
      .catch(() => {
        if (!cancelled) setGoogleEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const completeGoogleAuth = useCallback(
    async ({ token, user, exchange_code: exchangeCode, is_new_user: isNewUser }) => {
      if (exchangeCode) {
        const { data } = await api.post('/auth/google/exchange', { exchange_code: exchangeCode });
        persistGoogleAuthSession({ token: data.token, user: data.user });
        if (data.is_new_user) queueOnboardingAfterSignup(data.user.id);
        toast.success('Signed in with Google');
        navigate('/dashboard', { replace: true });
        return;
      }
      if (token && user) {
        persistGoogleAuthSession({ token, user });
        if (isNewUser) queueOnboardingAfterSignup(user.id);
        toast.success('Signed in with Google');
        navigate('/dashboard', { replace: true });
      }
    },
    [navigate],
  );

  const handleGoogleResult = useCallback(
    async ({ status, message, token, user, exchange_code: exchangeCode, is_new_user: isNewUser }) => {
      setGoogleLoading(false);
      googlePopupRef.current = null;
      if (status === 'success') {
        try {
          await completeGoogleAuth({
            token,
            user,
            exchange_code: exchangeCode,
            is_new_user: isNewUser,
          });
        } catch (err) {
          const detail = err.response?.data?.detail || '';
          if (
            detail.includes('Invalid or expired sign-in link') &&
            localStorage.getItem('storycraft_token')
          ) {
            navigate('/dashboard');
            return;
          }
          toast.error(detail || 'Google sign-in failed');
        }
        return;
      }
      if (
        message === 'oauth_code_invalid' &&
        localStorage.getItem('storycraft_token')
      ) {
        navigate('/dashboard');
        return;
      }
      const oauthErrors = {
        token_exchange:
          'Google token exchange failed. Check GOOGLE_AUTH_REDIRECT_URI matches Google Cloud Console exactly, then restart the backend.',
        oauth_redirect_mismatch:
          'Redirect URI mismatch. Open http://localhost:8000/api/auth/google/status — copy redirect_uri and client_id into the SAME Web OAuth client in Google Cloud.',
        oauth_code_invalid:
          'That Google sign-in link was already used. Close the popup completely, then click Continue with Google again.',
      };
      const detail =
        message === 'oauth_popup_closed'
          ? 'Sign-in window closed before Twinnee received the result.'
          : oauthErrors[message] ||
            (message && message !== 'oauth_error' ? message : 'Google sign-in failed');
      toast.error(detail, { duration: 8000 });
    },
    [completeGoogleAuth, navigate],
  );

  useEffect(() => {
    if (isDesktopApp()) return undefined;
    return subscribeGoogleAuthResult({
      popupRef: googlePopupRef,
      onResult: handleGoogleResult,
      onPopupClosed: () => {
        if (!googleLoading) return;
        setGoogleLoading(false);
        googlePopupRef.current = null;
      },
    });
  }, [googleLoading, handleGoogleResult]);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const returnPath = '/dashboard';
      const { data } = await api.post('/auth/google/begin', {
        return_path: returnPath,
        oauth_mode: 'popup',
        frontend_origin: resolveGoogleAuthFrontendOrigin(),
      });
      if (!data?.authorization_url) {
        toast.error('Could not start Google sign-in.');
        setGoogleLoading(false);
        return;
      }

      const desktopOAuthWait =
        isDesktopApp() && typeof window.twinTales?.waitForOAuthPopupResult === 'function'
          ? window.twinTales.waitForOAuthPopupResult()
          : null;

      const popup = openGoogleAuthWindow(data.authorization_url);
      if (!popup && !desktopOAuthWait) {
        window.location.href = data.authorization_url;
        return;
      }

      googlePopupRef.current = popup;

      if (desktopOAuthWait) {
        const payload = await desktopOAuthWait;
        await handleGoogleResult({
          status: String(payload?.status || ''),
          message: String(payload?.message || ''),
          token: payload?.token,
          user: payload?.user,
          exchange_code: payload?.exchange_code,
          is_new_user: payload?.is_new_user,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Google sign-in is not available');
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const payload = mode === 'login' ? { email, password } : { email, password, name, phone };
      const { data } = await api.post(endpoint, payload);
      localStorage.setItem('storycraft_token', data.token);
      localStorage.setItem('storycraft_user', JSON.stringify(data.user));
      import('@/lib/journeyTracker').then(({ identifyJourneySession }) => identifyJourneySession());
      if (mode === 'register' && data.user?.id) {
        queueOnboardingAfterSignup(data.user.id);
      }
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 sm:p-6 relative overflow-hidden font-sora bg-[#05070a]">
      {/* Soft Blurred Background Image Layer */}
      <div
        className="absolute inset-0 pointer-events-none z-0 scale-[1.02] blur-[3px]"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.7)), url('${process.env.PUBLIC_URL || ''}/LandingPageAssets/AuthBG.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />

      {/* Background Vignette Shadow Overlay */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background: 'radial-gradient(circle at center, transparent 15%, rgba(0, 0, 0, 0.8) 90%), linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, transparent 60%)',
        }}
      />

      {/* Background Ambient Glow Behind Card */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[650px] rounded-full pointer-events-none z-0 opacity-30"
        style={{
          background: 'radial-gradient(circle at center, rgba(45, 212, 191, 0.3) 0%, rgba(139, 92, 246, 0.15) 45%, transparent 70%)',
          filter: 'blur(90px)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 220, damping: 25 }}
        className="w-full max-w-[420px] relative z-10"
      >
        {/* Main Transparent Glass Auth Card with Deep Shadows */}
        <div className="rounded-[12px] border border-[#2DD4BF]/45 bg-black/[0.28] backdrop-blur-md p-7 sm:p-9 shadow-[0_25px_80px_rgba(0,0,0,0.85),0_0_50px_rgba(45,212,191,0.18)]">
          {/* Header Branding */}
          <div className="text-center mb-6">
            <Link
              to="/"
              aria-label="Twinnee home"
              className="inline-flex items-center justify-center hover:opacity-90 transition-opacity mb-2 mx-auto"
            >
              <TwinneeWordmark className="h-11" />
            </Link>

            {mode === 'login' ? (
              <p className="text-xs text-gray-400 font-body mt-1">
                Sign in to your studio
              </p>
            ) : (
              <div className="mt-1">
                <p className="text-[10px] font-mono tracking-widest text-[#2DD4BF] uppercase mb-1">
                  CINEMATIC NARRATIVE
                </p>
                <h2 className="text-lg font-bold text-white font-headline">Join the Studio</h2>
                <p className="text-xs text-gray-400 font-body">Begin your creative journey today.</p>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 font-body">
            {/* Full Name (Register Only) */}
            {mode === 'register' && (
              <div className="space-y-1.5">
                <label className="text-[11px] font-mono font-semibold tracking-wider text-gray-300 uppercase block">
                  Full Name
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Director's Name"
                    className="w-full h-11 pl-10 pr-4 rounded-[8px] bg-black/35 border border-white/15 text-white text-xs placeholder:text-gray-500 focus:border-[#2DD4BF]/70 focus:outline-none transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Email Address */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-mono font-semibold tracking-wider text-gray-300 uppercase block">
                Email Address
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={mode === 'login' ? 'creator@studio.com' : 'creator@twinnee.ai'}
                  className="w-full h-11 pl-10 pr-4 rounded-[8px] bg-black/35 border border-white/15 text-white text-xs placeholder:text-gray-500 focus:border-[#2DD4BF]/70 focus:outline-none transition-colors"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-mono font-semibold tracking-wider text-gray-300 uppercase block">
                  Password
                </label>
                {mode === 'login' && (
                  <Link
                    to="/forgot-password"
                    className="text-[11px] text-gray-300 hover:text-[#2DD4BF] transition-colors"
                  >
                    Forgot?
                  </Link>
                )}
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type={showPass ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-11 pl-10 pr-10 rounded-[8px] bg-black/35 border border-white/15 text-white text-xs placeholder:text-gray-500 focus:border-[#2DD4BF]/70 focus:outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors cursor-pointer"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Terms Checkbox (Register Only) */}
            {mode === 'register' && (
              <div className="flex items-start gap-2.5 pt-1">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="mt-0.5 rounded border-white/20 bg-white/5 text-[#2DD4BF] focus:ring-0 cursor-pointer"
                />
                <label htmlFor="agreeTerms" className="text-[11px] text-gray-400 leading-tight cursor-pointer">
                  I agree to the <span className="text-white hover:underline">Terms of Service</span> and{' '}
                  <span className="text-white hover:underline">Privacy Policy</span>.
                </label>
              </div>
            )}

            {/* Submit CTA Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading || (mode === 'register' && !agreeTerms)}
                className="w-full py-3 rounded-[8px] bg-gradient-to-r from-[#D0BCFF] via-[#A78BFA] to-[#62FAE3] text-[#06080d] font-bold text-xs font-headline tracking-wider hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-[#2DD4BF]/20 cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <span>{mode === 'login' ? 'Sign In' : 'Create Account'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>

          {/* Google Sign-in Alternative */}
          {googleEnabled && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                className="w-full py-2.5 rounded-[8px] border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-white text-xs font-semibold flex items-center justify-center gap-2.5 transition-colors cursor-pointer"
              >
                <GoogleMark />
                <span>Continue with Google</span>
              </button>
            </div>
          )}

          {/* Toggle between Login & Register */}
          <div className="mt-6 text-center text-xs text-gray-400 font-body">
            {mode === 'login' ? (
              <p>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('register')}
                  className="text-white font-semibold hover:text-[#2DD4BF] underline transition-colors cursor-pointer"
                >
                  Create one
                </button>
              </p>
            ) : (
              <p>
                Already a creator?{' '}
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-white font-semibold hover:text-[#2DD4BF] underline transition-colors cursor-pointer"
                >
                  Sign In
                </button>
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
